import type { MixDetailsDto, MixTrackDto, AudioClipDto } from '../dtos';

/**
 * Global Audio Engine for multi-track playback.
 */
export class AudioEngine {
  private static instance: AudioEngine;
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private masterAnalyser: AnalyserNode | null = null;
  private buffers: Map<string, AudioBuffer> = new Map();
  private trackNodes: Map<number, { gain: GainNode; panner: StereoPannerNode; analyser: AnalyserNode }> = new Map();
  private trackLevelBuffer: Uint8Array<ArrayBuffer> = new Uint8Array(new ArrayBuffer(256));
  private activeSources: AudioBufferSourceNode[] = [];
  private isPlaying: boolean = false;
  private startTime: number = 0;
  private pauseOffset: number = 0;

  // Recording state
  private mediaStream: MediaStream | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];
  private isRecording: boolean = false;
  private currentInputDeviceId: string | null = null;
  private currentInputChannel: 'stereo' | 'left' | 'right' = 'stereo';
  // Web Audio nodes used to extract a single channel out of a stereo mic stream.
  // Created lazily in startRecording when channel !== 'stereo'.
  private recordingSourceNode: MediaStreamAudioSourceNode | null = null;
  private recordingSplitter: ChannelSplitterNode | null = null;
  private recordingDestination: MediaStreamAudioDestinationNode | null = null;

  // Metronome state
  private metronomeTimer: number | null = null;
  private metronomeNextTickTime: number = 0;
  private metronomeBeatCount: number = 0;
  private metronomeBpm: number = 120;
  private metronomeBeatsPerBar: number = 4;
  private metronomeGain: GainNode | null = null;

  private constructor() {}

  public static getInstance(): AudioEngine {
    if (!AudioEngine.instance) {
      AudioEngine.instance = new AudioEngine();
    }
    return AudioEngine.instance;
  }

  /**
   * Initializes the AudioContext (must be called from a user interaction).
   */
  public init() {
    if (!this.ctx) {
      this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      // Master Chain: ... -> masterGain -> masterAnalyser -> destination
      this.masterGain = this.ctx.createGain();
      this.masterAnalyser = this.ctx.createAnalyser();
      this.masterAnalyser.fftSize = 256;
      
      this.masterGain.connect(this.masterAnalyser);
      this.masterAnalyser.connect(this.ctx.destination);
    }
    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
  }

  /**
   * Loads an audio file from a URL, decodes it, and caches it.
   */
  public async loadClip(clip: AudioClipDto, baseUrl: string): Promise<AudioBuffer> {
    const url = `${baseUrl}${clip.filePath}`;
    if (this.buffers.has(url)) return this.buffers.get(url)!;

    this.init();
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to load audio (${response.status}): ${url}`);
    const arrayBuffer = await response.arrayBuffer();
    const audioBuffer = await this.ctx!.decodeAudioData(arrayBuffer);
    
    this.buffers.set(url, audioBuffer);
    return audioBuffer;
  }

  /**
   * Pre-loads all clips for a mix.
   */
  public async preloadMix(mix: MixDetailsDto, baseUrl: string) {
    const loadPromises: Promise<any>[] = [];
    for (const track of mix.tracks) {
      for (const clip of track.clips) {
        loadPromises.push(
          this.loadClip(clip, baseUrl).catch(err => {
            console.warn(`Skipping clip ${clip.id}:`, err);
          })
        );
      }
    }
    await Promise.allSettled(loadPromises);
  }

  /**
   * Plays the mix from a specific time.
   */
  public play(mix: MixDetailsDto, startTimeSeconds: number = 0, baseUrl: string) {
    if (this.isPlaying) this.stop();
    this.init();

    this.isPlaying = true;
    this.startTime = this.ctx!.currentTime - startTimeSeconds;
    this.pauseOffset = startTimeSeconds;

    const anySolo = mix.tracks.some(t => t.isSolo);

    for (const track of mix.tracks) {
      const trackNodes = this.getOrCreateTrackNodes(track);

      // Update track nodes with current UI values (respect solo)
      const audible = !track.isMuted && (!anySolo || track.isSolo);
      trackNodes.gain.gain.value = audible ? track.volume : 0;
      trackNodes.panner.pan.value = track.pan;

      for (const clip of track.clips) {
        const buffer = this.buffers.get(`${baseUrl}${clip.filePath}`);
        if (!buffer) continue;

        // Only schedule if the clip ends after the start time
        const clipEnd = clip.timelineStart + clip.duration;
        if (clipEnd <= startTimeSeconds) continue;

        const source = this.ctx!.createBufferSource();
        source.buffer = buffer;
        source.connect(trackNodes.gain);

        // Calculate how much of the clip to skip (if playback started mid-clip)
        const skipOffset = Math.max(0, startTimeSeconds - clip.timelineStart);
        
        // Calculate when to start playing relative to AudioContext.currentTime
        const whenToStart = this.ctx!.currentTime + Math.max(0, clip.timelineStart - startTimeSeconds);
        
        // The point inside the audio file to start from
        const offsetInFile = clip.sourceOffset + skipOffset;
        
        // How much left to play
        const remainingDuration = Math.max(0, clip.duration - skipOffset);

        source.start(whenToStart, offsetInFile, remainingDuration);
        this.activeSources.push(source);
      }
    }
  }

  /**
   * Stops all playback.
   */
  public stop() {
    this.activeSources.forEach(source => {
      try { source.stop(); } catch(e) {}
    });
    this.activeSources = [];
    this.isPlaying = false;
    this.stopMetronome();
  }

  /**
   * Update volume for a track in real-time.
   */
  public updateTrackVolume(trackId: number, volume: number, isMuted: boolean) {
    const nodes = this.trackNodes.get(trackId);
    if (nodes) {
      nodes.gain.gain.setTargetAtTime(isMuted ? 0 : volume, this.ctx!.currentTime, 0.05);
    }
  }

  /**
   * Applies solo logic across all tracks. If any track is solo'd, non-solo tracks are silenced.
   */
  public applySoloState(tracks: MixTrackDto[]) {
    const anySolo = tracks.some(t => t.isSolo);
    for (const track of tracks) {
      const nodes = this.trackNodes.get(track.id);
      if (!nodes) continue;
      const audible = !track.isMuted && (!anySolo || track.isSolo);
      nodes.gain.gain.setTargetAtTime(audible ? track.volume : 0, this.ctx!.currentTime, 0.05);
    }
  }

  /**
   * Update pan for a track in real-time.
   */
  public updateTrackPan(trackId: number, pan: number) {
    const nodes = this.trackNodes.get(trackId);
    if (nodes) {
      nodes.panner.pan.setTargetAtTime(pan, this.ctx!.currentTime, 0.05);
    }
  }

  private getOrCreateTrackNodes(track: MixTrackDto) {
    this.init(); // Ensure masterGain exists
    if (this.trackNodes.has(track.id)) return this.trackNodes.get(track.id)!;

    const gain = this.ctx!.createGain();
    const panner = this.ctx!.createStereoPanner();
    const analyser = this.ctx!.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.3;

    // Track Graph: Source -> Gain -> Panner -> Analyser -> MasterGain
    gain.connect(panner);
    panner.connect(analyser);
    analyser.connect(this.masterGain!);

    const nodes = { gain, panner, analyser };
    this.trackNodes.set(track.id, nodes);
    return nodes;
  }

  /**
   * Returns the current peak level for a track in [0, 1+]. >1 indicates clipping.
   */
  public getTrackLevel(trackId: number): number {
    const nodes = this.trackNodes.get(trackId);
    if (!nodes) return 0;
    nodes.analyser.getByteTimeDomainData(this.trackLevelBuffer);
    let peak = 0;
    for (let i = 0; i < this.trackLevelBuffer.length; i++) {
      const sample = (this.trackLevelBuffer[i]! - 128) / 128;
      const abs = Math.abs(sample);
      if (abs > peak) peak = abs;
    }
    return peak;
  }

  public startMetronome(bpm: number, beatsPerBar: number, atTimeSeconds: number = 0) {
    this.init();
    this.stopMetronome();

    this.metronomeBpm = Math.max(20, Math.min(300, bpm));
    this.metronomeBeatsPerBar = Math.max(1, Math.min(16, beatsPerBar));

    if (!this.metronomeGain) {
      this.metronomeGain = this.ctx!.createGain();
      this.metronomeGain.gain.value = 0.6;
      this.metronomeGain.connect(this.masterGain!);
    }

    const beatDur = 60 / this.metronomeBpm;
    const offsetBeats = atTimeSeconds / beatDur;
    const nextBeatIndex = Math.ceil(offsetBeats);
    this.metronomeBeatCount = nextBeatIndex;
    const nextBeatPlaybackTime = nextBeatIndex * beatDur;
    this.metronomeNextTickTime = this.ctx!.currentTime + (nextBeatPlaybackTime - atTimeSeconds);

    this.metronomeTimer = window.setInterval(() => this.scheduleMetronomeTicks(), 25);
    this.scheduleMetronomeTicks();
  }

  public stopMetronome() {
    if (this.metronomeTimer !== null) {
      clearInterval(this.metronomeTimer);
      this.metronomeTimer = null;
    }
  }

  private scheduleMetronomeTicks() {
    if (!this.ctx) return;
    const lookahead = 0.1;
    const beatDur = 60 / this.metronomeBpm;
    while (this.metronomeNextTickTime < this.ctx.currentTime + lookahead) {
      const isDownbeat = this.metronomeBeatCount % this.metronomeBeatsPerBar === 0;
      this.scheduleClick(this.metronomeNextTickTime, isDownbeat);
      this.metronomeNextTickTime += beatDur;
      this.metronomeBeatCount++;
    }
  }

  private scheduleClick(when: number, isDownbeat: boolean) {
    if (!this.ctx || !this.metronomeGain) return;
    const osc = this.ctx.createOscillator();
    const env = this.ctx.createGain();
    osc.frequency.value = isDownbeat ? 1500 : 900;
    osc.type = 'square';
    env.gain.setValueAtTime(0, when);
    env.gain.linearRampToValueAtTime(isDownbeat ? 1.0 : 0.6, when + 0.001);
    env.gain.exponentialRampToValueAtTime(0.0001, when + 0.05);
    osc.connect(env);
    env.connect(this.metronomeGain);
    osc.start(when);
    osc.stop(when + 0.06);
  }

  public get currentPlaybackTime(): number {
    if (!this.isPlaying || !this.ctx) return this.pauseOffset;
    return this.ctx.currentTime - this.startTime;
  }

  /**
   * Returns current master level (0.0 to 1.0)
   */
  public getMasterLevel(): number {
    if (!this.masterAnalyser) return 0;
    const dataArray = new Uint8Array(this.masterAnalyser.frequencyBinCount);
    this.masterAnalyser.getByteTimeDomainData(dataArray);
    
    let max = 0;
    for (let i = 0; i < dataArray.length; i++) {
      const v = Math.abs((dataArray[i] as number) - 128) / 128;
      if (v > max) max = v;
    }
    return max;
  }

  /**
   * Extracts peaks from an AudioBuffer for waveform rendering.
   */
  public getWaveformData(buffer: AudioBuffer, width: number, clipDuration?: number, sourceOffset = 0): number[] {
    if (buffer.numberOfChannels === 0) return new Array(width).fill(0);
    const data = buffer.getChannelData(0);
    // For MP3s, decodeAudioData can produce a buffer much longer than the actual audio
    // due to decoder padding. Use the clip's stored duration to limit the sample range.
    const startSample = Math.max(0, Math.floor(sourceOffset * buffer.sampleRate));
    const endSampleByDuration = clipDuration !== undefined
      ? startSample + Math.floor(clipDuration * buffer.sampleRate)
      : data.length;
    const endSample = Math.min(endSampleByDuration, data.length);
    const sampleCount = Math.max(0, endSample - startSample);
    const step = Math.max(1, Math.floor(sampleCount / width));
    const peaks = [];

    for (let i = 0; i < width; i++) {
      let max = 0;
      for (let j = 0; j < step; j++) {
        const index = startSample + i * step + j;
        if (index >= endSample) break;
        const datum = Math.abs(data[index] as number);
        if (datum > max) max = datum;
      }
      peaks.push(max);
    }
    return peaks;
  }

  /**
   * Requests microphone access. Call early so permission is granted before recording.
   * Re-acquires the stream if the requested device or channel differs from what's
   * currently active. Caller passes the user's preferred device + channel so the
   * profile's recording settings flow through to capture.
   */
  public async requestMicAccess(opts?: { deviceId?: string | null; channel?: 'stereo' | 'left' | 'right' }): Promise<boolean> {
    const deviceId = opts?.deviceId ?? null;
    const channel = opts?.channel ?? 'stereo';

    // No-op if we already have an active stream with the same configuration.
    if (this.mediaStream && deviceId === this.currentInputDeviceId && channel === this.currentInputChannel) {
      return true;
    }

    this.releaseInput();

    try {
      const audioConstraints: MediaTrackConstraints = {
        autoGainControl: false,
        noiseSuppression: false,
        echoCancellation: false,
      };
      // `ideal` instead of `exact` so a saved deviceId from another browser/device
      // (profile is synced cross-device) silently falls back to the default mic
      // here rather than throwing OverconstrainedError.
      if (deviceId) audioConstraints.deviceId = { ideal: deviceId };
      // Need stereo at the source so the splitter can isolate a single side.
      if (channel !== 'stereo') audioConstraints.channelCount = 2;

      this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
      this.currentInputDeviceId = deviceId;
      this.currentInputChannel = channel;
      return true;
    } catch (e) {
      console.error('Microphone access denied or device unavailable:', e);
      return false;
    }
  }

  private releaseInput() {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(t => t.stop());
      this.mediaStream = null;
    }
    if (this.recordingSourceNode) {
      try { this.recordingSourceNode.disconnect(); } catch {}
      this.recordingSourceNode = null;
    }
    if (this.recordingSplitter) {
      try { this.recordingSplitter.disconnect(); } catch {}
      this.recordingSplitter = null;
    }
    if (this.recordingDestination) {
      try { this.recordingDestination.disconnect(); } catch {}
      this.recordingDestination = null;
    }
    this.currentInputDeviceId = null;
    this.currentInputChannel = 'stereo';
  }

  /**
   * Starts recording from the microphone. Should be called alongside play().
   * For mono channel selection (left/right), routes the mic stream through a
   * ChannelSplitter so MediaRecorder only captures the chosen side — useful when
   * the audio interface presents both inputs as a stereo pair (e.g., MOTU M2)
   * and you only want one input.
   */
  public startRecording(): boolean {
    if (!this.mediaStream) return false;
    this.init();

    this.recordedChunks = [];
    this.isRecording = true;

    let recordableStream: MediaStream;
    if (this.currentInputChannel === 'stereo') {
      recordableStream = this.mediaStream;
    } else {
      this.recordingSourceNode = this.ctx!.createMediaStreamSource(this.mediaStream);
      this.recordingSplitter = this.ctx!.createChannelSplitter(2);
      this.recordingDestination = this.ctx!.createMediaStreamDestination();
      this.recordingSourceNode.connect(this.recordingSplitter);
      const channelIndex = this.currentInputChannel === 'left' ? 0 : 1;
      this.recordingSplitter.connect(this.recordingDestination, channelIndex, 0);
      recordableStream = this.recordingDestination.stream;
    }

    this.mediaRecorder = new MediaRecorder(recordableStream, {
      mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm'
    });

    this.mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) this.recordedChunks.push(e.data);
    };

    this.mediaRecorder.start(100); // Collect data every 100ms
    return true;
  }

  /**
   * Stops recording and returns a Promise that resolves with the recorded File.
   */
  public stopRecordingAsync(): Promise<File | null> {
    if (!this.mediaRecorder || !this.isRecording) return Promise.resolve(null);

    return new Promise<File | null>((resolve) => {
      this.mediaRecorder!.onstop = () => {
        if (this.recordedChunks.length === 0) {
          resolve(null);
          return;
        }
        const blob = new Blob(this.recordedChunks, { type: this.mediaRecorder!.mimeType });
        const ext = blob.type.includes('webm') ? 'webm' : 'ogg';
        const file = new File([blob], `recording-${Date.now()}.${ext}`, { type: blob.type });
        this.recordedChunks = [];
        resolve(file);
      };
      this.mediaRecorder!.stop();
      this.isRecording = false;
    });
  }

  public get recording(): boolean {
    return this.isRecording;
  }

  public get hasMicAccess(): boolean {
    return this.mediaStream !== null;
  }

  public get state() {
    return {
      isPlaying: this.isPlaying,
      currentTime: this.currentPlaybackTime
    };
  }

  /**
   * Renders the mix offline using OfflineAudioContext, respecting mute/solo/gain/pan.
   * Returns a WAV Blob.
   */
  public async renderOffline(mix: MixDetailsDto, durationSeconds: number, baseUrl: string): Promise<Blob> {
    const sampleRate = 44100;
    const length = Math.ceil(sampleRate * durationSeconds);
    const offlineCtx = new OfflineAudioContext(2, length, sampleRate);

    const masterGain = offlineCtx.createGain();
    masterGain.connect(offlineCtx.destination);

    const anySolo = mix.tracks.some(t => t.isSolo);

    for (const track of mix.tracks) {
      const audible = !track.isMuted && (!anySolo || track.isSolo);
      if (!audible) continue;

      const gain = offlineCtx.createGain();
      const panner = offlineCtx.createStereoPanner();
      gain.gain.value = track.volume;
      panner.pan.value = track.pan;
      gain.connect(panner);
      panner.connect(masterGain);

      for (const clip of track.clips) {
        const buffer = this.buffers.get(`${baseUrl}${clip.filePath}`);
        if (!buffer) continue;

        const source = offlineCtx.createBufferSource();
        source.buffer = buffer;
        source.connect(gain);
        source.start(clip.timelineStart, clip.sourceOffset, clip.duration);
      }
    }

    const rendered = await offlineCtx.startRendering();
    return this.audioBufferToWav(rendered);
  }

  public async renderTrackOffline(track: MixTrackDto, durationSeconds: number, baseUrl: string): Promise<Blob> {
    const sampleRate = 44100;
    const length = Math.ceil(sampleRate * durationSeconds);
    const offlineCtx = new OfflineAudioContext(2, length, sampleRate);

    const gain = offlineCtx.createGain();
    const panner = offlineCtx.createStereoPanner();
    gain.gain.value = track.volume;
    panner.pan.value = track.pan;
    gain.connect(panner);
    panner.connect(offlineCtx.destination);

    for (const clip of track.clips) {
      const buffer = this.buffers.get(`${baseUrl}${clip.filePath}`);
      if (!buffer) continue;

      const source = offlineCtx.createBufferSource();
      source.buffer = buffer;
      source.connect(gain);
      source.start(clip.timelineStart, clip.sourceOffset, clip.duration);
    }

    const rendered = await offlineCtx.startRendering();
    return this.audioBufferToWav(rendered);
  }

  private audioBufferToWav(buffer: AudioBuffer): Blob {
    const numChannels = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    const length = buffer.length;
    const bytesPerSample = 2; // 16-bit
    const dataSize = length * numChannels * bytesPerSample;
    const headerSize = 44;
    const arrayBuffer = new ArrayBuffer(headerSize + dataSize);
    const view = new DataView(arrayBuffer);

    // WAV header
    const writeString = (offset: number, str: string) => {
      for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
    };
    writeString(0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true); // chunk size
    view.setUint16(20, 1, true); // PCM
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * numChannels * bytesPerSample, true);
    view.setUint16(32, numChannels * bytesPerSample, true);
    view.setUint16(34, 16, true); // bits per sample
    writeString(36, 'data');
    view.setUint32(40, dataSize, true);

    // Interleave channels and convert to 16-bit PCM
    const channels = [];
    for (let ch = 0; ch < numChannels; ch++) {
      channels.push(buffer.getChannelData(ch));
    }

    let offset = 44;
    for (let i = 0; i < length; i++) {
      for (let ch = 0; ch < numChannels; ch++) {
        const sample = Math.max(-1, Math.min(1, channels[ch]![i]!));
        view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
        offset += 2;
      }
    }

    return new Blob([arrayBuffer], { type: 'audio/wav' });
  }
}

export const audioEngine = AudioEngine.getInstance();
